import type { CommandInput, CommandName, Event, Snapshot } from "@melo/shared";
import { store } from "./store";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      credentials: "same-origin",
      ...init,
      headers: { "x-melo-client": "web", ...(init.body && !(init.body instanceof FormData) ? { "content-type": "application/json" } : {}), ...init.headers },
    });
  } catch {
    throw new ApiError(0, "network", "Can't reach melo.build. Check your connection.");
  }
  if (res.status === 401) {
    store.setStatus("signed-out");
    throw new ApiError(401, "unauthorized", "Your session ended. Sign in again.");
  }
  const body = (await res.json().catch(() => null)) as { error?: { code: string; message: string; details?: unknown } } | null;
  if (!res.ok) {
    throw new ApiError(res.status, body?.error?.code ?? "internal", body?.error?.message ?? `Request failed (${res.status}).`, body?.error?.details);
  }
  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: "POST", body: form }),
  snapshot: () => request<Snapshot>("/v1/snapshot"),
  eventsSince: (since: number) => request<{ events: Event[]; truncated: boolean }>(`/v1/events?since=${since}`),
  activity: (spaceId: string, limit = 60) => request<{ events: Event[] }>(`/v1/spaces/${spaceId}/activity?limit=${limit}`),
  command: <N extends CommandName>(type: N, input: CommandInput<N>, clientId: string) =>
    request<{ ok: true; result: unknown; events: Event[] }>("/v1/commands", { method: "POST", body: JSON.stringify({ type, input, clientId }) }),
};
